import Cookies from 'js-cookie';
import type { IArticleConfig } from '~/config/article';
import { ENV } from '~/config/article/env';
import widgetsConfig from '~/config/widgets.config';
import { createMarker, debugLog, markers, measures, measureTimeBetweenMarks } from '~/debug';
import type CommentsIframe from '~/modules/iframes/Comments';
import type IframelessComments from '~/modules/IframelessElements/Comments';
import type { IIframeInterface } from '~/modules/iframes/Iframe';
import ServerLogger from '~/modules/ServerLogger';
import Session from '~/modules/Session';
import TokenHandler from '~/modules/TokenHandler';
import type WidgetsCreator from '~/modules/WidgetsCreator';
import { advancedKeys } from '~/config/keys';
import type { AudioWidgetListener } from './CommunicationListeners/AudioWidgetListener';
import { CommentsListener } from './CommunicationListeners/CommentsListener';
import { IframelessCommentsListener } from './CommunicationListeners/IframelessCommentsListener';
import { EmotesListener } from './CommunicationListeners/EmotesListener';
import type { ModalListener } from './CommunicationListeners/ModalListener';
import type { PowerbarListener } from './CommunicationListeners/PowerbarListener';
import type { SubscribeListener } from './CommunicationListeners/SubscribeListener';
import { QuizListener } from './CommunicationListeners/QuizListener';
import AdUnitCreator from '~/modules/Advertisement/AdUnitCreator';
// import type QuizNewsfeedPopup from '~/modules/QuizNewsfeedPopup';
import { safeLocalStorageGet } from '~/utils/localStorage';
import type { IAdvancedConfig } from '~/modules/Advertisement/types';
import QuizIframe from '~/modules/iframes/Quiz';
import { makeRequest } from '~/utils/general';
import { getMetaContentByName, isInCrossOriginFrame, isInIframe } from '~/utils/dom';
import type { IIframelessWidgetInterface } from '~/modules/IframelessElements/IframelessWidget';

// 🤝🏾 Interfaces
// import { cachedKeys } from '~/config/keys';
type Widgets = 'comments' | 'powerbar' | 'emotes' | 'subscribe' | 'modal' | 'audio' | 'quiz';

class CommunicationCreator {
  /**
   * @param {string} index - elements index selector. We use this to fill [data=vuukle-count] elements with comments count
   * once we receive a message with comments count from a comments widget.
   * TODO: Possible needs moving to another place ⬆
   * @param {IIframeInterface | undefined} commentsIframe - comments iFrame to add listener and share messages with
   * @param {IIframelessWidgetInterface | undefined} iframelessComments - iframeless comments element to add a listener and share messages with
   * @param {IIframeInterface | undefined} emotesIframe - emotes iFrame to attach listener and share messages with
   * @param {IIframeInterface[] | []} powerbars - All available powerbars iFrames to attach listeners and share messages with
   * @param {IIframeInterface[] | []} subscribeWidgets - All available subscribe widget iFrames to attach listeners and share messages with
   * @param {IIframeInterface[] | []} audioWidgets - All available audio widget iframes to attach listeners and share messages with
   * @param {IIframeInterface | undefined} quizWidget - quiz iframe to add listener and share messages with
   * @param {IArticleConfig} articleConfig - current article config to use
   * @param {WidgetsCreator} widgetsCreator - widget creator class object
   * @param {CommentsListener | undefined} commentsListener - comments communication listener
   * @param {IframelessCommentsListener | undefined} iframelessCommentsListener - iframeless comments communication listener
   * @param {QuizListener | undefined} quizListener - quiz communication listener
   * @param {PowerbarListener[]} powerbarListeners - sharebar communication listeners
   * @param {EmotesListener | undefined} emotesListener - emotes communication listener
   * @param {ModalListener | undefined} modalListener - modal communication listener
   * @param {SubscribeListener[]} subscribeListeners - subscribe widget communication listeners
   * @param {AudioWidgetListener[]} audioListeners - audio widget communication listeners
   */
  private constructor(
    private readonly index: string,
    private readonly commentsIframe: IIframeInterface | undefined,
    private readonly iframelessComments: IIframelessWidgetInterface | undefined,
    private readonly emotesIframe: IIframeInterface | undefined,
    private readonly powerbars: IIframeInterface[] | [],
    private readonly subscribeWidgets: IIframeInterface[] | [],
    private readonly audioWidgets: IIframeInterface[] | [],
    private quizWidget: IIframeInterface | undefined,
    private readonly articleConfig: IArticleConfig,
    // @ts-ignore
    private readonly widgetsCreator: WidgetsCreator,
    commentsListener: CommentsListener | undefined,
    iframelessCommentsListener: IframelessCommentsListener | undefined,
    quizListener: QuizListener | undefined,
    powerbarListeners: PowerbarListener[],
    emotesListener: EmotesListener | undefined,
    modalListener: ModalListener | undefined,
    subscribeListeners: SubscribeListener[],
    audioListeners: AudioWidgetListener[]
  ) {
    if (commentsListener) {
      this.commentsListener = commentsListener;
    }
    if (iframelessCommentsListener) {
      this.iframelessCommentsListener = iframelessCommentsListener;
    }
    if (quizListener) {
      this.quizListener = quizListener;
    }
    this.powerbarListeners = powerbarListeners;
    if (emotesListener) {
      this.emotesListener = emotesListener;
      this.emotesListener.setCommunicationCreatorInstance(this);
    }
    if (modalListener) {
      this.modalListener = modalListener;
    }
    this.subscribeListeners = subscribeListeners;
    this.audioListeners = audioListeners;
    this.requestInitialData();
    Session.addCookieConsentListener('-' + index, (value: boolean) => this.changeCookieSettingsForWidgets(value));
    Session.addGeoListener('-' + index, (value: any) =>
      this.shareMessageWithWidgets(['comments', 'powerbar', 'audio'], { geoData: value })
    );
    ServerLogger.addIPListener('-' + index, (value: any) =>
      this.shareMessageWithWidgets(['comments', 'powerbar', 'audio'], { ip: value })
    );
    this.shareMessageWithWidgets(['comments', 'powerbar', 'audio'], { country: Session.userCountry });
    this.shareMessageWithWidgets(['comments', 'powerbar', 'audio'], { userGUID: Session.userAPIKey });

    import(/* webpackChunkName: "abd" */ '~/modules/Advertisement/AdblockDetector').then((adblockDetectorModule) => {
      const adblockDetector = new adblockDetectorModule.default(articleConfig, {}, this);
      adblockDetector.detectAdBlockForWidgets();
    });
  }

  private modalIframe: IIframeInterface;
  private commentsListener: CommentsListener;
  private iframelessCommentsListener: IframelessCommentsListener;
  private quizListener: QuizListener;
  private powerbarListeners: PowerbarListener[] = [];
  private emotesListener: EmotesListener;
  private modalListener: ModalListener;
  private subscribeListeners: SubscribeListener[] = [];
  private static popupCreated: boolean = false;
  // private static quizNewsfeedPopupCreated: boolean = false;
  private audioListeners: AudioWidgetListener[] = [];
  public static sendGAEvents: boolean = false;
  // public static quizNewsfeedPopupInstance: QuizNewsfeedPopup;

  /** Indicates if the platform has already received comment count before user clicked on 'Load new comments' */
  public static commentCountLock = false;

  public static commentInputBoxRect: DOMRect;
  /** Number of comments user posted */
  public static postedCommentCount = 0;

  /** Widgets that have both light mode and dark mode support and are currently on the page */
  public static widgetsOnPageWithThemeSupport: {
    [key: string]: {
      commentsListener?: CommentsListener;
      iframelessCommentsListener?: IframelessCommentsListener;
      emotesListener?: EmotesListener;
      powerbarListeners?: PowerbarListener[];
      modalListener?: ModalListener;
      quizListener?: QuizListener;
    };
  } = {};

  public static responseGroups: string | null = null;
  public static quizWidgetsOnThePage: Map<string, QuizListener> = new Map();
  public static sendResponseGroupsToQuizWidgets = () => {
    this.quizWidgetsOnThePage.forEach((quiz) => {
      quiz.postMessage({ responseGroups: CommunicationCreator.responseGroups });
    });
  };

  /**
   * Create an instance of CommunicationCreator module
   * @param {string} index article index
   * @param {IIframeInterface | undefined} commentsIframe comments iframe
   * @param {IIframelessWidgetInterface | undefined} iframelessComments iframeless comments object
   * @param {IIframeInterface | undefined} emotesIframe emotes iframe
   * @param {IIframeInterface[] | []} powerbars sharebar iframes
   * @param {IIframeInterface[] | []} subscribeWidgets subscribe widget iframes
   * @param {IIframeInterface[] | []} audioWidgets audio widget iframes
   * @param {IIframeInterface | undefined} quizWidget quiz widget iframe
   * @param {IArticleConfig} articleConfig article config object
   * @param {IRealtimeInterface} realtime realtime analytics module
   * @param {WidgetsCreator} widgetsCreator widgets creator instance
   * @returns {Promise<CommunicationCreator>}
   */
  public static async createCommunicationCreatorInstance(
    index: string,
    commentsIframe: IIframeInterface | undefined,
    iframelessComments: IIframelessWidgetInterface | undefined,
    emotesIframe: IIframeInterface | undefined,
    powerbars: IIframeInterface[] | [],
    subscribeWidgets: IIframeInterface[] | [],
    audioWidgets: IIframeInterface[] | [],
    quizWidget: IIframeInterface | undefined,
    articleConfig: IArticleConfig,
    widgetsCreator: WidgetsCreator
  ): Promise<CommunicationCreator> {
    let commentsListener: CommentsListener | undefined;
    let iframelessCommentsListener: IframelessCommentsListener | undefined;
    let quizListener: QuizListener | undefined;
    const powerbarListeners: PowerbarListener[] = [];
    let emotesListener: EmotesListener | undefined;
    let modalListener: ModalListener | undefined;
    const subscribeListeners: SubscribeListener[] = [];
    const audioListeners: AudioWidgetListener[] = [];
    createMarker(markers.commsCreatorStart);
    measureTimeBetweenMarks(measures.platformStartToCommsStart, markers.platformStart, markers.commsCreatorStart);
    /** Initialize message listeners */
    if (powerbars.length > 0) {
      for (const powerbar of powerbars) {
        const importedModule = await import(/* webpackChunkName: "pbl" */ './CommunicationListeners/PowerbarListener');
        powerbarListeners.push(
          new importedModule.PowerbarListener(
            powerbar,
            powerbars.filter((pb: IIframeInterface) => pb.id !== powerbar.id),
            index,
            articleConfig,
            widgetsCreator,
            modalListener
          )
        );
      }
      CommunicationCreator.widgetsOnPageWithThemeSupport[index] = {};
      CommunicationCreator.widgetsOnPageWithThemeSupport[index].powerbarListeners =
        CommunicationCreator.widgetsOnPageWithThemeSupport[index].powerbarListeners || [];
      powerbarListeners.forEach((powerbarListener) => {
        CommunicationCreator.widgetsOnPageWithThemeSupport[index].powerbarListeners?.push(powerbarListener);
      });
    }

    if (audioWidgets.length > 0) {
      for (const audioWidget of audioWidgets) {
        const importedModule = await import(
          /* webpackChunkName: "awl" */ './CommunicationListeners/AudioWidgetListener'
        );
        audioListeners.push(
          new importedModule.AudioWidgetListener(
            audioWidget,
            audioWidgets.filter((audio: IIframeInterface) => audio.id !== audioWidget.id),
            articleConfig,
            powerbarListeners,
            modalListener,
            widgetsCreator
          )
        );
      }
      powerbarListeners.forEach((listener) => {
        listener.updateAudioListenerInstance(audioListeners);
      });
    }

    if (emotesIframe) {
      emotesListener = new EmotesListener(emotesIframe, articleConfig, powerbarListeners);
      if (!CommunicationCreator.widgetsOnPageWithThemeSupport[index]) {
        CommunicationCreator.widgetsOnPageWithThemeSupport[index] = {};
      }
      CommunicationCreator.widgetsOnPageWithThemeSupport[index].emotesListener = emotesListener;
    }

    if (quizWidget) {
      quizListener = new QuizListener(
        quizWidget,
        modalListener,
        articleConfig,
        index,
        widgetsCreator,
        emotesListener,
        powerbarListeners
      );
      if (!CommunicationCreator.widgetsOnPageWithThemeSupport[index]) {
        CommunicationCreator.widgetsOnPageWithThemeSupport[index] = {};
      }
      CommunicationCreator.widgetsOnPageWithThemeSupport[index].quizListener = quizListener;
      CommunicationCreator.quizWidgetsOnThePage.set(index, quizListener);
      quizListener.postMessage({ responseGroups: CommunicationCreator.responseGroups });
    }

    if (commentsIframe) {
      commentsListener = new CommentsListener(
        commentsIframe,
        index,
        articleConfig,
        widgetsCreator,
        emotesListener,
        powerbarListeners,
        audioListeners,
        quizListener,
        modalListener
      );
      if (!CommunicationCreator.widgetsOnPageWithThemeSupport[index]) {
        CommunicationCreator.widgetsOnPageWithThemeSupport[index] = {};
      }
      CommunicationCreator.widgetsOnPageWithThemeSupport[index].commentsListener = commentsListener;
      powerbarListeners.forEach((powerbar) => {
        if (commentsListener) {
          powerbar.updateCommentsListenerInstance(commentsListener);
        }
      });
      audioListeners.forEach((audio) => {
        if (commentsListener) {
          audio.updateCommentsListenerInstance(commentsListener);
        }
      });
      quizListener?.updateCommentsListenerInstance(commentsListener);
    }

    if (iframelessComments) {
      iframelessCommentsListener = new IframelessCommentsListener(
        iframelessComments,
        index,
        articleConfig,
        widgetsCreator,
        emotesListener,
        powerbarListeners,
        audioListeners,
        quizListener,
        modalListener
      );
      if (!CommunicationCreator.widgetsOnPageWithThemeSupport[index]) {
        CommunicationCreator.widgetsOnPageWithThemeSupport[index] = {};
      }
      CommunicationCreator.widgetsOnPageWithThemeSupport[index].iframelessCommentsListener = iframelessCommentsListener;
      powerbarListeners.forEach((powerbar) => {
        if (iframelessCommentsListener) {
          powerbar.updateIframelessCommentsListenerInstance(iframelessCommentsListener);
        }
      });
      audioListeners.forEach((audio) => {
        if (iframelessCommentsListener) {
          audio.updateIframelessCommentsInstance(iframelessCommentsListener);
        }
      });
      if (iframelessCommentsListener) {
        quizListener?.updateIframelessCommentsInstance(iframelessCommentsListener);
      }
    }

    if (subscribeWidgets.length > 0) {
      for (const subscribe of subscribeWidgets) {
        const importedModule = await import(/* webpackChunkName: "sbl" */ './CommunicationListeners/SubscribeListener');
        subscribeListeners.push(new importedModule.SubscribeListener(subscribe));
      }
    }

    createMarker(markers.commsCreatorEnd);
    measureTimeBetweenMarks(measures.commsStartToEnd, markers.commsCreatorStart, markers.commsCreatorEnd);
    measureTimeBetweenMarks(measures.platformStartToCommsEnd, markers.platformStart, markers.commsCreatorEnd);
    return new CommunicationCreator(
      index,
      commentsIframe,
      iframelessComments,
      emotesIframe,
      powerbars,
      subscribeWidgets,
      audioWidgets,
      quizWidget,
      articleConfig,
      widgetsCreator,
      commentsListener,
      iframelessCommentsListener,
      quizListener,
      powerbarListeners,
      emotesListener,
      modalListener,
      subscribeListeners,
      audioListeners
    );
  }

  /**
   * Share data with widget(s)
   * @param widgets
   * @param message
   */
  private shareMessageWithWidgets(widgets: Widgets[], message: any) {
    const listeners = {
      comments: this.commentsListener,
      iframelessComments: this.iframelessCommentsListener,
      emotes: this.emotesListener,
      modal: this.modalListener,
      quiz: this.quizListener,
    };

    const bulkListeners = {
      powerbar: this.powerbarListeners,
      subscribe: this.subscribeListeners,
      audio: this.audioListeners,
    };

    for (let widget of widgets) {
      listeners[widget as keyof typeof listeners]?.postMessage(message);
      if (widget === 'comments') {
        listeners.iframelessComments?.postMessage(message);
      }
    }

    for (let widget of widgets) {
      const bulkListener: PowerbarListener[] | SubscribeListener[] | AudioWidgetListener[] | undefined =
        bulkListeners[widget as keyof typeof bulkListeners];

      if (bulkListener) {
        bulkListener.forEach((listener) => {
          listener.postMessage(message);
        });
      }
    }
  }

  public activateCTAInQuiz(ctaConfig: IAdvancedConfig['quizCallToAction']) {
    this.quizListener?.enableCTAPopup(ctaConfig);
  }

  public showEmotesArticlesByDefault() {
    this.emotesListener?.postMessage({ loadArticlesByDefault: true });
  }

  public isQuizOnThePage() {
    return Boolean(this.quizWidget && this.quizListener);
  }

  /**
   * Remove communication message listeners
   */
  public removeMessageListeners() {
    const listeners = [this.commentsListener, this.emotesListener, this.modalListener, this.quizListener];
    const bulkListeners = [this.powerbarListeners, this.subscribeListeners, this.audioListeners];

    for (let widget of listeners) {
      widget?.removeMessageListener();
    }

    for (let widget of bulkListeners) {
      widget?.forEach((widgetListener) => widgetListener.removeMessageListener());
    }
  }

  /**
   * Show/Hide the article play button in widgets
   * @param show
   */
  public toggleAudioButton(show: boolean) {
    this.shareMessageWithWidgets(['comments'], { showAudioButton: show });
  }

  /** Send a communication message to widgets about adblocker being enabled */
  public tellWidgetsAboutAdblock() {
    this.shareMessageWithWidgets(['comments', 'modal'], { adblockEnabled: true });
    this.commentsListener?.setIsAdblockDetected(true);
    this.iframelessCommentsListener?.setIsAdblockDetected(true);
  }

  public enableQuizCompactMode() {
    this.shareMessageWithWidgets(['quiz'], { compactMode: true });
    (this.quizWidget as QuizIframe)?.changeHeight(250);
    (this.quizWidget as QuizIframe)?.changeWidth(250);
    this.quizListener?.setFormerHeight(250);
  }

  public enable300x250QuizCompactMode() {
    this.shareMessageWithWidgets(['quiz'], { compact300x250: true });
    (this.quizWidget as QuizIframe)?.changeHeight(250);
    (this.quizWidget as QuizIframe)?.changeWidth(300);
    this.quizListener?.setFormerHeight(250);
  }

  /**
   * Change Quiz max height
   * @param height
   */
  public tellQuizToChangeMaxHeight(height: number) {
    this.shareMessageWithWidgets(['quiz'], { setMaxHeightTo: height });
    (this.quizWidget as QuizIframe)?.changeHeight(height);
    this.quizListener?.setFormerHeight(height);
  }

  /**
   * Change Quiz max width
   * @param width
   */
  public tellQuizToChangeMaxWidth(width: number) {
    this.shareMessageWithWidgets(['quiz'], { setMaxWidthTo: width });
    (this.quizWidget as QuizIframe)?.changeWidth(width);
  }

  /**
   * Send a message to widgets about placing ads inside them
   * @param canCreate
   */
  public toggleAdSpaceCreation(canCreate: boolean) {
    this.shareMessageWithWidgets(['comments'], { canCreateAdSpace: canCreate });
  }

  /** Send anon token to CW */
  private tellCWToPostAnonToken() {
    this.shareMessageWithWidgets(['comments'], { anonToken: TokenHandler.getAnonToken() });
  }

  /**
   * Enable audio ads by sending the publisher ID for Google IMA to powerbar and comments
   * @param audioID
   */
  public enableAudioAds(audioID: string) {
    this.shareMessageWithWidgets(['powerbar', 'comments', 'audio'], { audioAdID: audioID });
  }

  public async checkForAdblockers() {
    const adblockDetector = await import(/* webpackChunkName: "abd" */ '~/modules/Advertisement/AdblockDetector');
    if (!adblockDetector.default.adblockDetectionHasExecuted) {
      new adblockDetector.default(this.articleConfig, advancedKeys[this.articleConfig.apiKey], this);
    }
  }

  public passCustomFontToWidgets(customFont: IAdvancedConfig['customFont']) {
    this.shareMessageWithWidgets(['quiz'], { customFont });
  }

  public addCustomStylesQuiz(customQuizStyles: IAdvancedConfig['customQuizStyles']) {
    this.shareMessageWithWidgets(['quiz'], { customQuizStyles });
  }

  public hideAddVuukle() {
    this.shareMessageWithWidgets(['comments'], { hideAddVuukle: true });
  }

  public hideVuukleLogoInFooter() {
    this.shareMessageWithWidgets(['comments'], { hideVuukleLogoInFooter: true });
  }

  public enableAudienceTrackingPixel(
    networkCode: string,
    audienceSegments: {
      topic: string;
      segment_id: string;
    }[]
  ) {
    this.quizListener?.enableAudienceTrackingPixel(networkCode, audienceSegments);
  }

  public trackTimeSpentOnQuiz() {
    this.quizListener?.trackTimeSpentOnWidget();
  }

  public addCommentsPanelTrigger(selector: string) {
    if (this.commentsListener) {
      this.commentsListener.addCustomTriggerForCommentsPanel(selector);
    }

    if (this.iframelessCommentsListener) {
      this.iframelessCommentsListener.addCustomTriggerForCommentsPanel(selector);
    }
  }

  /**
   * Fetch data from loadVuukle
   * @param options
   * @param options.quizOverride
   * @returns
   */
  private async getDataFromVuukleServers(options: { quizOverride: boolean } = { quizOverride: false }) {
    const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;
    const modifiedTime = getMetaContentByName('property', 'article:modified_time', windowContext);
    const data = await makeRequest(
      'GET',
      `${ENV.API_URL}/api/v1/Comments/loadVuukle`,
      {
        apiKey: this.articleConfig.apiKey,
        articleId: this.articleConfig.articleId,
        globalRecommendation: this.articleConfig.globalRecommendations,
        host: this.articleConfig.host,
        start: 0,
        uri: this.articleConfig.url,
        quizEnabled: options.quizOverride || Boolean(this.quizWidget),
        ...(Boolean(this.quizWidget)
          ? { quizLngCode: document.documentElement.lang || 'en', title: this.articleConfig.title }
          : {}),
        ...(modifiedTime ? { modifiedTime } : {}),
      },
      'text/plain'
    );
    return data;
  }

  /**
   * Initiate a popup with Quiz and Newsfeed widgets
   * @param quizData
   */
  // private async initiateQuizNewsfeedPopup(quizData: any[]) {
  //   if (!CommunicationCreator.quizNewsfeedPopupCreated) {
  //     CommunicationCreator.quizNewsfeedPopupCreated = true;
  //     const QuizNewsfeedPopup = (await import(/* webpackChunkName: "qnp" */ '~/modules/QuizNewsfeedPopup')).default;
  //     CommunicationCreator.quizNewsfeedPopupInstance = new QuizNewsfeedPopup(
  //       quizData,
  //       this.articleConfig,
  //       this.widgetsCreator
  //     );
  //   }
  // }

  public async createQuizInstanceNearElement(element: HTMLElement) {
    if (
      this.quizWidget ||
      document.querySelector(widgetsConfig.selectors.quiz.concat(this.index)) ||
      advancedKeys[this.articleConfig.apiKey].excludeFromEmptyEmotesQuizInsertion ||
      !this.articleConfig.quiz.enabled
    ) {
      return;
    }

    const quizDiv = document.createElement('div');
    quizDiv.id = widgetsConfig.selectors.quiz.slice(1).concat(this.index);

    element.insertAdjacentElement('afterend', quizDiv);

    const quizIframe = new QuizIframe('quiz-iframe'.concat(this.index), this.articleConfig);
    quizDiv.appendChild(quizIframe.iframe);

    this.quizWidget = quizIframe;

    const quizListener = new QuizListener(
      this.quizWidget,
      this.modalListener,
      this.articleConfig,
      this.index,
      this.widgetsCreator,
      this.emotesListener,
      this.powerbarListeners
    );
    this.quizListener = quizListener;
    if (!CommunicationCreator.widgetsOnPageWithThemeSupport[this.index]) {
      CommunicationCreator.widgetsOnPageWithThemeSupport[this.index] = {};
    }
    CommunicationCreator.widgetsOnPageWithThemeSupport[this.index].quizListener = quizListener;
    CommunicationCreator.quizWidgetsOnThePage.set(this.index, quizListener);
    quizListener.postMessage({ responseGroups: CommunicationCreator.responseGroups });

    await this.requestInitialData();
    this.applyQuizConfig(advancedKeys[this.articleConfig.apiKey], false, this.index);
    this.modalListener?.updateQuizListenerInstance(this.quizListener);
    this.commentsListener?.updateQuizListenerInstance(this.quizListener);
    this.iframelessCommentsListener?.updateQuizListenerInstance(this.quizListener);
  }

  /** Request Vuukle Settings of a publisher */
  private async requestPublicConfig() {
    try {
      const publisherConfigRequest = await makeRequest(
        'GET',
        `${ENV.API_URL}/api/v1/Publishers/PublicConfigs`,
        {
          apiKey: this.articleConfig.apiKey,
          host: this.articleConfig.host,
        },
        'text/plain'
      );
      if (!publisherConfigRequest.success) {
        throw new Error(publisherConfigRequest.errors[0]);
      }

      if (publisherConfigRequest.data.geo) {
        Session.determineCookieUsability(
          publisherConfigRequest.data.geo.countryCode || null,
          publisherConfigRequest.data.geo.region || null
        );
      }

      /** Ask user to subscribe to web notification topics if the feature is enabled and they haven't done it already  */
      if (
        publisherConfigRequest.data.settings.comments.browserNotificationEnabled &&
        !Cookies.get('vuukle_notification_subscription') &&
        !Cookies.get('vuukle_notification_subscription_dismissed') &&
        window.ServiceWorker
      ) {
        const webNotificationPopup = (await import(/* webpackChunkName: "webntfpop" */ '../WebNotificationPopup'))
          .WebNotificationPopup;
        new webNotificationPopup();
      }

      /** Remove ads from the page if they're disabled */
      if (typeof publisherConfigRequest.data.settings.ads === 'boolean' && !publisherConfigRequest.data.settings.ads) {
        AdUnitCreator.removeAllAds();
      }

      /** If we're not on the index page of any website - we should proceed with getting the IAB topic */
      if (window.location.pathname !== '/') {
        /** Fetch IAB Topic of the current article */
        const iabTopicRequest = await makeRequest(
          'GET',
          `${ENV.API_URL}/api/v1/Articles/GetIabTopic`,
          {
            host: this.articleConfig.host,
            articleId: this.articleConfig.articleId,
          },
          'text/plain'
        );
        /** Make it globally available */
        if (iabTopicRequest.data?.article?.iabTopic) {
          window._vuukleIabtopic = iabTopicRequest.data.article.iabTopic;
        }
      }

      // Initiate quiz + newsfeed popup if configured
      // if (
      //   !CommunicationCreator.quizNewsfeedPopupCreated &&
      //   publisherConfigRequest.data.settings.article.interactivityHub
      // ) {
      //   try {
      //     const loadVuukleData = await this.getDataFromVuukleServers({ quizOverride: true });
      //     if (loadVuukleData.data.quiz) {
      //       this.initiateQuizNewsfeedPopup(loadVuukleData.data);
      //     }
      //   } catch (e) {
      //     debugLog('error', 'Unable to initiate Quiz and Newsfeed widgets popup');
      //   }
      // }
    } catch (e) {
      Session.determineCookieUsability(null, null);
      if (e.message) {
        console.error('[Platform.js] Error', e.message);
      }
    }
  }

  public confirmCookieNotice() {
    this.shareMessageWithWidgets(['comments', 'quiz'], { cookieNoticeConfirmed: true });
  }

  public changeCommentsBubblePosition(position: 'left' | 'right') {
    if (this.commentsIframe) {
      (this.commentsIframe as CommentsIframe).changeBubblePosition(position);
    }

    if (this.iframelessComments) {
      (this.iframelessComments as IframelessComments).changeBubblePosition(position);
    }
  }

  public changeCommentsBubblePersistence(isPersistent: boolean) {
    if (this.commentsIframe) {
      (this.commentsIframe as CommentsIframe).makeBubblePersistent(isPersistent);
    }

    if (this.iframelessComments) {
      (this.iframelessComments as IframelessComments).makeBubblePersistent(isPersistent);
    }
  }

  /** Request initial widget data from loadVuukle */
  private async requestInitialData() {
    if (
      !this.commentsIframe &&
      !this.iframelessComments &&
      !this.emotesIframe &&
      !this.quizWidget &&
      this.powerbars.length === 0 &&
      this.audioWidgets.length === 0 &&
      this.subscribeWidgets.length === 0 &&
      !document.querySelector(widgetsConfig.selectors.newsfeed.concat(this.index))
    ) {
      Session.isArticleBrandSafe = null;
      this.requestPublicConfig();
      /** No point in fetching the initial data from loadVuukle if there are no widgets that require the data */
      return;
    }

    try {
      // /** Fetch data from Cloudflare KV, otherwise fetch it from Vuukle servers */
      // if (cachedKeys.includes(this.articleConfig.apiKey)) {
      //   const data = await makeRequest(
      //     'GET',
      //     `https://cdn.vuukle.com/loadVuukle`,
      //     {
      //       articleId: this.articleConfig.articleId,
      //       host: this.articleConfig.host,
      //     },
      //     'text/plain'
      //   );
      //   /** If we got the cached data - proceed, otherwise fetch it normally */
      //   if (data.success) {
      //     this.shareMessageWithWidgets(['comments', 'emotes', 'powerbar', 'subscribe'], { loadVuukle: data.data });
      //   } else {
      //     const vuukleServerData = await getDataFromVuukleServers();
      //     this.shareInitialDataWithWidgets(vuukleServerData.data);
      //   }
      // } else {
      // }
      const data = await this.getDataFromVuukleServers();
      if (!data.success) {
        throw new Error(data.errors[0]);
      }

      if (data.data.geo) {
        Session.determineCookieUsability(data.data.geo.countryCode || null, data.data.geo.region || null);
      }

      /** Remove ads from the page if they're disabled */
      if (typeof data.data.settings.ads === 'boolean' && !data.data.settings.ads) {
        AdUnitCreator.removeAllAds();
      }

      this.shareMessageWithWidgets(['comments', 'emotes', 'powerbar', 'subscribe', 'audio', 'quiz', 'modal'], {
        loadVuukle: data.data,
      });

      this.commentsListener?.setLoadVuukleData(data.data);
      /** Make loadVuukle settings accessible by non-iframe widgets */
      window.getLoadVuukleSettings = () => {
        return data.data.settings;
      };

      if (data.data.settings.theme.color) {
        this.commentsListener?.setThemeColor(data.data.settings.theme.color);
        this.iframelessCommentsListener?.setThemeColor(data.data.settings.theme.color);
        this.quizListener?.setThemeColor(data.data.settings.theme.color);
        this.powerbarListeners?.forEach((listener) => listener.setThemeColor(data.data.settings.theme.color));
      }

      // Make IABTopic information accessible in the global scope for advertisement targeting
      if (data.data.article.iabTopic) {
        window._vuukleIabtopic = data.data.article.iabTopic;
      }

      // Set whether the article is safe to use for ad campaigns with googletag
      if (typeof data.data.article.isArticleBrandSafe !== 'undefined') {
        Session.isArticleBrandSafe = data.data.article.isArticleBrandSafe;
        window._vuukleArticleBrandSafe =
          data.data.article.isArticleBrandSafe === null ? 'null' : data.data.article.isArticleBrandSafe.toString();
        const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;
        windowContext.googletag = windowContext.googletag || {};
        windowContext.googletag.cmd = windowContext.googletag.cmd || [];
        windowContext.googletag.cmd.push(() => {
          windowContext.googletag
            .pubads()
            .setTargeting(
              'isArticleBrandSafe',
              data.data.article.isArticleBrandSafe === null ? 'null' : data.data.article.isArticleBrandSafe.toString()
            );
        });
      }

      /** Check whether some widgets are enabled. If they are not - remove them */
      if (data.data.settings.enabledFeatures) {
        const enabledWidgets: string[] = data.data.settings.enabledFeatures;

        if (!enabledWidgets.includes('CommentsWidget')) {
          this.commentsIframe?.iframe.remove();
          this.iframelessComments?.hostDiv.remove();
        }

        if (!enabledWidgets.includes('EmotesWidget')) {
          this.emotesIframe?.iframe.remove();
        }

        if (!enabledWidgets.includes('ShareBar')) {
          this.powerbars.forEach((powerbar: IIframeInterface) => powerbar.iframe.remove());
        }
      }

      const applyCommentsConfigs = async (commentsInstance: CommentsIframe | IframelessComments) => {
        if (data.data.settings.article.showCommentCount) {
          commentsInstance.createScrollListenerForCommentBubble(
            typeof data.data.article?.commentCount === 'number' ? data.data.article?.commentCount : 0,
            data.data.settings.comments.commentBubbleLoadType === 0,
            data.data.settings.comments.loadCommentBubbleAfter,
            data.data.settings.comments.loadCommentBubbleAt
          );
        }

        // Make min-height of CW smaller when it's collapsed
        if (data.data.settings.comments.hideCommentWidget && !advancedKeys[this.articleConfig.apiKey]?.compact) {
          commentsInstance.changeMinHeight(80);
        }
      };

      // Create a subscription box popup that will be shown to the user when their mouse pointer leaves the page
      if (data.data.settings.article.showNewsletterPopUp && !CommunicationCreator.popupCreated) {
        const SubscribePopup = (await import(/* webpackChunkName: "sbp" */ '~/modules/SubscribePopup')).default;
        new SubscribePopup(data.data.settings.quiz.quizLogo);
        CommunicationCreator.popupCreated = true;
      }
      // Update comment counters
      CommunicationCreator.fillVuukleCountElement(this.index, data.data.article?.commentCount || 0);
      if (this.commentsIframe) {
        applyCommentsConfigs(this.commentsIframe as CommentsIframe);
      }

      if (this.iframelessComments) {
        applyCommentsConfigs(this.iframelessComments as IframelessComments);
      }

      // Pass the quiz to the quiz widget
      if (data.data.quiz && this.quizListener) {
        // if (data.data.settings.article.interactivityHub) {
        //   this.initiateQuizNewsfeedPopup(data.data);
        // }
        if (!data.data.article.quizDisabled) {
          this.quizListener.postMessage({ quiz: data.data.quiz });
        }

        this.quizListener.postMessage({ questionsAnsweredByUser: safeLocalStorageGet('vuukle_quiz_answers') });
        this.quizListener.postMessage({ frequencyCaps: safeLocalStorageGet('vuukle_quiz_frequency_cap') });

        if (data.data.quiz.length > 0 && data.data.article.quizDisabled) {
          this.quizListener.postMessage({ fallbackQuiz: data.data.quiz });
        }

        if (data.data.quiz.length === 0) {
          this.quizListener.postMessage({ noQuiz: true });
        }
        // if (data.data.article.iabTopic) {
        //   // For topics that have multiple words, we need to use dashes (-) instead of spaces
        //   const topicWithDashes = data.data.article.iabTopic.replace(/\s+/g, '-');
        //   if (window.gtag) {
        //     window.gtag('event', 'quizzly_1p_data', { event_category: `quizzly_${topicWithDashes}` });
        //   } else {
        //     window.dataLayer = window.dataLayer || [];
        //     window.dataLayer.push(['event', 'quizzly_1p_data', { event_category: `quizzly_${topicWithDashes}` }]);
        //   }
        // }
      }

      if (!data.data.quiz && data.data.article.quizDisabled && this.quizListener) {
        this.quizListener.postMessage({ noQuiz: true });
      }

      // Initiate quiz + newsfeed popup if configured
      // if (
      //   !CommunicationCreator.quizNewsfeedPopupCreated &&
      //   !data.data.quiz &&
      //   data.data.settings.article.interactivityHub &&
      //   !data.data.article.quizDisabled
      // ) {
      //   scheduler.postTask(async () => {
      //     try {
      //       const loadVuukleData = await this.getDataFromVuukleServers({ quizOverride: true });
      //       if (loadVuukleData.data.quiz) {
      //         this.initiateQuizNewsfeedPopup(loadVuukleData.data);
      //       }
      //     } catch (e) {
      //       debugLog('error', 'Unable to initiate Quiz and Newsfeed widgets popup');
      //     }
      //   });
      // }

      if (data.data.loginTypes.includes('anonymous')) {
        TokenHandler.setAnonymousCommenting(true);
        TokenHandler.setRegularCommenting(false);
        TokenHandler.setGuestCommenting(false);
        this.tellCWToPostAnonToken();
      }

      /** Ask user to subscribe to web notification topics if the feature is enabled and they haven't done it already  */
      if (
        data.data.settings.comments.browserNotificationEnabled &&
        !Cookies.get('vuukle_notification_subscription') &&
        !Cookies.get('vuukle_notification_subscription_dismissed') &&
        window.ServiceWorker
      ) {
        const webNotificationPopup = (await import(/* webpackChunkName: "webntfpop" */ '../WebNotificationPopup'))
          .WebNotificationPopup;
        new webNotificationPopup();
      }
    } catch (e) {
      Session.isArticleBrandSafe = null;
      Session.determineCookieUsability(null, null);
      /** In case loadVuukle requests fail, notify widgets about it */
      if (this.commentsListener) {
        this.commentsListener.postMessage({ fetchFailLabel: true });
      } else if (this.iframelessCommentsListener) {
        this.iframelessCommentsListener.postMessage({ fetchFailLabel: true });
      } else if (this.powerbarListeners) {
        this.powerbarListeners.forEach((listener) => {
          listener.postMessage({ fetchFailLabel: true });
        });
      }
      this.powerbarListeners.forEach((listener) => {
        listener.postMessage({ fetchFail: true });
      });
      if (this.quizListener) {
        this.quizListener.postMessage({ noQuiz: true });
      }
      if (e.message === 'incorrect_apiKey') {
        console.error('[Platform.js] ApiKey is not correct!');
      }
      //  else {
      //   NotificationAlert.createNotification('Domain not registered, implementation error!', 'error');
      // }
    }
  }

  /**
   * Enable/Disable cookie usage in widgets
   * @param canUseCookies
   */
  public changeCookieSettingsForWidgets(canUseCookies: boolean) {
    this.shareMessageWithWidgets(['comments', 'emotes', 'powerbar', 'modal', 'quiz'], { canUseCookies });
    Session.canUseCookies = canUseCookies;
  }

  /**
   * Enable heat map analysis after the user interacts with Quiz
   */
  public enableHeatMapAnalysis() {
    this.quizListener?.enableHeatMapAnalysis();
  }

  public enableRecommendedQuizzes() {
    this.shareMessageWithWidgets(['quiz'], { enableRecommendedQuizzes: true });
  }

  /**
   * Disable quiz log in requirement for seeing the quiz score
   */
  public disableQuizLogin() {
    this.shareMessageWithWidgets(['quiz'], { disableLogin: true });
  }

  /**
   * Change Quiz to only show sponsored questions
   */
  public makeQuizQuestionsSponsoredOnly() {
    this.shareMessageWithWidgets(['quiz'], { sponsoredOnly: true });
  }

  public changeQuizSponsoredQuestionPosition(position: number) {
    this.shareMessageWithWidgets(['quiz'], { sponsoredQuizPosition: position });
  }

  public showAnswerCorrectnessOnLogin(showCorrectnessOnLogin: boolean) {
    this.shareMessageWithWidgets(['quiz'], { showRightOrWrongOnLogin: showCorrectnessOnLogin });
  }

  public hideTakeAnotherQuiz(hideTakeAnotherQuiz: boolean) {
    this.shareMessageWithWidgets(['quiz'], { hideTakeAnotherQuiz });
  }

  /**
   * Apply configurations to Quiz
   * @param {IAdvancedConfig} config Advanced ad configuration
   * @param runPostQuizCallback
   * @param index
   */
  public applyQuizConfig(config: IAdvancedConfig, runPostQuizCallback = true, index: string) {
    if (config.insertQuizAndAd?.quizOptions?.postQuizInsertionCallback && runPostQuizCallback) {
      config.insertQuizAndAd.quizOptions.postQuizInsertionCallback(index);
    }

    const adTargetsAreDefined = Boolean(config.targets);
    const adDivIsDefined = config.targets && Object.keys(config.targets).some((a) => a.includes('vuukle-ad'));

    if (
      this.widgetsCreator?.communicationCreator.isQuizOnThePage() &&
      !config.insertQuizAndAd &&
      adTargetsAreDefined &&
      adDivIsDefined
    ) {
      const stylesheet = document.createElement('style');
      stylesheet.innerHTML = `
          div[id*="vuukle-quiz"] + div[id*="vuukle-ad"] {
            min-height: 250px;
            min-width: 300px;
          }
        `;
      document.head.appendChild(stylesheet);
    }

    if (config.quizzlySponsoredSurveyPosition) {
      this.changeQuizSponsoredQuestionPosition(config.quizzlySponsoredSurveyPosition);
    }

    if (config.showSponsoredQuestionsOnly) {
      this.makeQuizQuestionsSponsoredOnly();
    }

    if (config.insertQuizAndAd?.quizOptions?.quizMaxHeight || config.quizMaxHeight) {
      this.tellQuizToChangeMaxHeight(config.insertQuizAndAd?.quizOptions?.quizMaxHeight || config.quizMaxHeight || 265);
    }

    if (config.insertQuizAndAd?.quizOptions?.quizMaxWidth || config.quizMaxWidth) {
      this.tellQuizToChangeMaxWidth(config.insertQuizAndAd?.quizOptions?.quizMaxWidth || config.quizMaxWidth || 300);
    }

    if (config.disableQuizLogin) {
      this.disableQuizLogin();
    }

    if (config.compactQuizzly && !Session.isMobileUser) {
      this.enableQuizCompactMode();
    }

    if (config.compactQuizzlyMobile && Session.isMobileUser) {
      this.enableQuizCompactMode();
    }

    if (config.compact300x250Quizzly) {
      if (
        (config.compact300x250Quizzly.desktop && !Session.isMobileUser) ||
        (config.compact300x250Quizzly.mobile && Session.isMobileUser)
      ) {
        this.enable300x250QuizCompactMode();
      }
    }

    if (config.moveQuizzly) {
      this.widgetsCreator?.moveQuizWidget(config.moveQuizzly);
    }

    if (config.trackTimeSpentOnQuiz) {
      this.trackTimeSpentOnQuiz();
    }

    if (config.trackQuizView) {
      this.trackQuizView();
    }

    // Send custom configured font to widgets
    if (
      config.customFont &&
      typeof config.customFont.fontFamily === 'string' &&
      (typeof config.customFont.url === 'string' || typeof config.customFont.externalFontStylesheetLink === 'string')
    ) {
      scheduler.postTask(() => {
        this.passCustomFontToWidgets(config.customFont);
      });
    }

    if (config.enableHeatMap) {
      this.enableHeatMapAnalysis();
    }

    if (config.enableRecommendedQuizzes) {
      this.enableRecommendedQuizzes();
    }

    if (config.showQuizAnswerRightOrWrongOnLogin) {
      this.showAnswerCorrectnessOnLogin(config.showQuizAnswerRightOrWrongOnLogin);
    }

    if (config.hideTakeAnotherQuiz) {
      this.hideTakeAnotherQuiz(config.hideTakeAnotherQuiz);
    }
  }

  /**
   * Fire Quiz view_quizzly BQ event
   */
  public trackQuizView() {
    (this.quizWidget as QuizIframe).fireQuizViewEvent();
  }

  /**
   * Send ad to emotes widget
   * @param emotesSlot The emote slot for the ad
   * @param emoteImageUrl The image url for the ad
   * @param adClickUrl The url that the ad will use for the url
   * @param label
   */
  public sendAdToEmotes(emotesSlot: 1 | 2 | 3 | 4 | 5 | 6, emoteImageUrl: string, adClickUrl: string, label: string) {
    this.shareMessageWithWidgets(['emotes'], {
      emotesAd: { emotesSlot, imageUrl: emoteImageUrl, clickUrl: adClickUrl, label },
    });
  }

  /** Enable realtime commenting/websocket connection */
  // public enableRealtime() {
  //   if (this.commentsIframe) {
  //     (this.commentsIframe as CommentsIframe).addRealtimeListener();
  //     (this.commentsIframe as CommentsIframe).createRealtimeBar();
  //     this.shareMessageWithWidgets(['comments'], { enableRealtime: true });
  //   }

  //   if (this.iframelessComments) {
  //     (this.iframelessComments as IframelessComments).addRealtimeListener();
  //     (this.iframelessComments as IframelessComments).createRealtimeBar();
  //     this.shareMessageWithWidgets(['comments'], { enableRealtime: true });
  //   }
  // }

  /**
   * Modal widget initialize
   * NOTE: We need to lazy load modal widget when user triggers first action for modal
   * @param {IIframeInterface} modalIframe - Modal IFrame
   * @param data
   * @returns {boolean}
   */
  public initializeModal = async (
    modalIframe: IIframeInterface | undefined,
    data: any | undefined
  ): Promise<boolean> => {
    if (modalIframe) {
      this.modalIframe = modalIframe;
      const importedModule = await import(/* webpackChunkName: "mdl" */ './CommunicationListeners/ModalListener');
      this.modalListener = new importedModule.ModalListener(
        this.modalIframe,
        this.articleConfig,
        this.powerbarListeners,
        this.commentsListener,
        this.iframelessCommentsListener,
        this.emotesListener,
        this.quizListener
      );
      this.modalListener.postMessage(data);
      CommunicationCreator.widgetsOnPageWithThemeSupport[this.index].modalListener = this.modalListener;
      this.commentsListener?.updateModalInstance(modalIframe, this.modalListener);
      this.iframelessCommentsListener?.updateModalInstance(modalIframe, this.modalListener);
      this.powerbarListeners.forEach((powerbarListener) =>
        powerbarListener.updateModalInstance(modalIframe, this.modalListener)
      );
      this.audioListeners.forEach((audioListener) =>
        audioListener.updateModalInstance(modalIframe, this.modalListener)
      );
      this.quizListener?.updateModalInstance(modalIframe, this.modalListener);
      return true;
    }
    return false;
  };

  /**
   * Fills [data-vuukle] or [data-vuukle-{index}] elements with comments count.
   * In case it's empty we get data-empty-text attribute text to show
   * @param {string} index - elements index
   * @param {number} count - comments count
   */
  public static fillVuukleCountElement(index: string, count: number) {
    const countElements = document.querySelectorAll(index ? `[data=vuukle-count${index}]` : '[data=vuukle-count]');
    countElements.forEach((element: HTMLElement) => {
      element.innerHTML = count <= 0 && element.dataset.emptyText ? element.dataset.emptyText : String(count);
    });
  }

  /**
   * ↕️ Add widgets resizing events listener to window object
   * ‼ ⚠ Should be called only ONCE. We call it right before export
   * @returns {void}
   */
  public static attachResizeListener(): void {
    window.addEventListener('message', (event) => {
      // Parse event
      let parsedEvent;
      try {
        parsedEvent = JSON.parse(event.data);
      } catch (_e) {
        parsedEvent = event.data;
      }
      // Process events
      if (parsedEvent.type === 'resize' && parsedEvent.sourceWindowName) {
        debugLog('log', 'Resize message received', event);
        const iframe = document.querySelector(`iframe[name="${parsedEvent.sourceWindowName}"]`);
        if (iframe instanceof HTMLIFrameElement) {
          iframe.style.height = `${parsedEvent.data.height}px`;
        }
      }
    });
  }

  /**
   * Remove widgets that are in the object for theme changing
   * @param index article index
   */
  public static removeWidgetsFromThemingObject(index: string) {
    delete CommunicationCreator.widgetsOnPageWithThemeSupport[index];
  }
}

// 🔴 Call resizing event attaching only once
CommunicationCreator.attachResizeListener();

// Add a function to the global scope to allow publishers to toggle the widget theme
window.changeVuukleWidgetsTheme = (theme: 'light' | 'dark') => {
  if (theme === 'dark' || theme === 'light') {
    Object.keys(CommunicationCreator.widgetsOnPageWithThemeSupport).forEach((widgetIndex) => {
      CommunicationCreator.widgetsOnPageWithThemeSupport[widgetIndex].commentsListener?.changeTheme(theme);
      CommunicationCreator.widgetsOnPageWithThemeSupport[widgetIndex].iframelessCommentsListener?.changeTheme(theme);
      CommunicationCreator.widgetsOnPageWithThemeSupport[widgetIndex].emotesListener?.changeTheme(theme);
      CommunicationCreator.widgetsOnPageWithThemeSupport[widgetIndex].powerbarListeners?.forEach((listener) =>
        listener.changeTheme(theme)
      );
      CommunicationCreator.widgetsOnPageWithThemeSupport[widgetIndex].modalListener?.changeTheme(theme);
      CommunicationCreator.widgetsOnPageWithThemeSupport[widgetIndex].quizListener?.changeTheme(theme);
    });
  }
};

window.sendResponseGroupsToQuizzly = () => {
  CommunicationCreator.responseGroups = safeLocalStorageGet('quizzly_surveys_response_groups');
  CommunicationCreator.sendResponseGroupsToQuizWidgets();
};

export default CommunicationCreator;
